import React from 'react';
import { Sliders, Sparkles, X } from 'lucide-react';
import type { AlgorithmId, AlgorithmTune } from '../../types/ambilight';
import { ALGORITHM_INFO_MAP } from '../../services/colorEngine';
import { useAmbilightContext } from '../../context/AmbilightContext';

export interface TunePopoverProps {
  currentAlgorithm?: AlgorithmId;
  tune?: AlgorithmTune;
  isOpen?: boolean;
  onClose?: () => void;
  onUpdateTune?: (key: keyof AlgorithmTune, val: number | boolean) => void;
}

export const TunePopover: React.FC<TunePopoverProps> = (props) => {
  const context = useAmbilightContext();

  const currentAlgorithm = props.currentAlgorithm ?? context.algorithm;
  const tune = props.tune ?? context.tune;
  const isOpen = props.isOpen ?? context.isTuneOpen;
  const onClose = props.onClose ?? (() => context.setIsTuneOpen(false));
  const onUpdateTune = props.onUpdateTune ?? context.updateTuneParam;

  if (!isOpen) return null;

  const info = ALGORITHM_INFO_MAP[currentAlgorithm];

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-backdrop select-none"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-sm rounded-2xl modal-card p-5 text-white shadow-2xl"
        style={{
          backgroundColor: '#0f1422',
          backgroundImage: 'linear-gradient(180deg, #141b2d 0%, #0d121f 100%)',
        }}
      >
        {/* Encabezado */}
        <div className="flex items-center justify-between pb-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Sliders className="w-4 h-4 text-purple-400" />
            <h4 className="text-sm font-semibold text-white">
              Ajuste Fino: <span className="text-purple-300">{info.name}</span>
            </h4>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Parámetros del Algoritmo */}
        <div className="my-4 space-y-4 text-xs">
          {currentAlgorithm === 'perceptual' && (
            <>
              {/* Selector de Perfil Perceptual */}
              <div>
                <span className="text-white/70 block mb-1.5 font-medium">Perfil de Scoring:</span>
                <div className="grid grid-cols-2 gap-1.5">
                  {[
                    { id: 'cinematic', label: '🎬 Cine', desc: 'Fondo + Anti-subtítulos' },
                    { id: 'ambient', label: '🍃 Ambiente', desc: 'Balance orgánico suave' },
                    { id: 'reactive', label: '⚡ Reactivo', desc: 'Explosiones y destellos' },
                    { id: 'game', label: '🎮 Gaming', desc: 'Anti-HUD + movimiento' },
                  ].map((p) => {
                    const isPActive = (tune.perceptualProfile || 'cinematic') === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => onUpdateTune('perceptualProfile' as any, p.id as any)}
                        className={`p-2 rounded-xl text-left border transition-all ${
                          isPActive
                            ? 'bg-purple-600/30 border-purple-400 text-white font-semibold shadow-sm'
                            : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'
                        }`}
                      >
                        <div className="text-[11px]">{p.label}</div>
                        <div className="text-[9px] text-white/40">{p.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Cobertura Sublineal (alpha) */}
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white/70">Cobertura Sublineal (C^α):</span>
                  <span className="font-mono text-purple-300 font-semibold">
                    {(tune.perceptualCoverageAlpha ?? 0.35).toFixed(2)}
                  </span>
                </div>
                <input
                  type="range"
                  min={0.15}
                  max={0.65}
                  step={0.05}
                  value={tune.perceptualCoverageAlpha ?? 0.35}
                  onChange={(e) => onUpdateTune('perceptualCoverageAlpha' as any, Number(e.target.value))}
                  className="w-full h-1.5 bg-slate-700 rounded-lg cursor-pointer accent-purple-400"
                />
                <p className="text-[10px] text-white/40 mt-1">
                  Menor valor: objetos pequeños (estrellas, fuego) compiten contra el fondo
                </p>
              </div>

              {/* Rechazo de Subtítulos */}
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white/70">Rechazo de Subtítulos:</span>
                  <span className="font-mono text-purple-300 font-semibold">
                    {Math.round((tune.perceptualTextPenalty ?? 0.90) * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min={0.0}
                  max={1.0}
                  step={0.05}
                  value={tune.perceptualTextPenalty ?? 0.90}
                  onChange={(e) => onUpdateTune('perceptualTextPenalty' as any, Number(e.target.value))}
                  className="w-full h-1.5 bg-slate-700 rounded-lg cursor-pointer accent-purple-400"
                />
                <p className="text-[10px] text-white/40 mt-1">
                  Penaliza texto blanco en la zona inferior para no contaminar el ambiente
                </p>
              </div>

              {/* Sensibilidad de Saliencia */}
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white/70">Sensibilidad de Saliencia:</span>
                  <span className="font-mono text-purple-300 font-semibold">
                    {(tune.perceptualSaliencyWeight ?? 1.0).toFixed(1)}x
                  </span>
                </div>
                <input
                  type="range"
                  min={0.2}
                  max={2.0}
                  step={0.1}
                  value={tune.perceptualSaliencyWeight ?? 1.0}
                  onChange={(e) => onUpdateTune('perceptualSaliencyWeight' as any, Number(e.target.value))}
                  className="w-full h-1.5 bg-slate-700 rounded-lg cursor-pointer accent-purple-400"
                />
              </div>

              {/* Eventos Dinámicos (Movimiento) */}
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white/70">Eventos Dinámicos (Movimiento):</span>
                  <span className="font-mono text-purple-300 font-semibold">
                    {(tune.perceptualMotionWeight ?? 0.8).toFixed(1)}x
                  </span>
                </div>
                <input
                  type="range"
                  min={0.0}
                  max={2.0}
                  step={0.1}
                  value={tune.perceptualMotionWeight ?? 0.8}
                  onChange={(e) => onUpdateTune('perceptualMotionWeight' as any, Number(e.target.value))}
                  className="w-full h-1.5 bg-slate-700 rounded-lg cursor-pointer accent-purple-400"
                />
                <p className="text-[10px] text-white/40 mt-1">
                  Realza explosiones y objetos que se mueven rápido
                </p>
              </div>
            </>
          )}

          {currentAlgorithm === 'weighted' && (
            <>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white/70">Exponente Beta (β):</span>
                  <span className="font-mono text-purple-300 font-semibold">{tune.weightedBeta.toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  min={0.5}
                  max={4.0}
                  step={0.05}
                  value={tune.weightedBeta}
                  onChange={(e) => onUpdateTune('weightedBeta', Number(e.target.value))}
                  className="w-full h-1.5 bg-slate-700 rounded-lg cursor-pointer accent-purple-400"
                />
                <p className="text-[10px] text-white/40 mt-1">
                  Mayor valor: más peso a los píxeles brillantes
                </p>
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white/70">Suelo de Sombra (Floor):</span>
                  <span className="font-mono text-purple-300 font-semibold">{tune.weightedFloor.toFixed(2)}</span>
                </div>
                <input
                  type="range"
                  min={0.0}
                  max={0.3}
                  step={0.01}
                  value={tune.weightedFloor}
                  onChange={(e) => onUpdateTune('weightedFloor', Number(e.target.value))}
                  className="w-full h-1.5 bg-slate-700 rounded-lg cursor-pointer accent-purple-400"
                />
                <p className="text-[10px] text-white/40 mt-1">
                  Peso mínimo que siguen teniendo los tonos oscuros
                </p>
              </div>
            </>
          )}

          {currentAlgorithm === 'dominant' && (
            <>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white/70">Niveles de Cuantización:</span>
                  <span className="font-mono text-purple-300 font-semibold">{tune.dominantLevels}</span>
                </div>
                <input
                  type="range"
                  min={4}
                  max={32}
                  step={1}
                  value={tune.dominantLevels}
                  onChange={(e) => onUpdateTune('dominantLevels', Number(e.target.value))}
                  className="w-full h-1.5 bg-slate-700 rounded-lg cursor-pointer accent-purple-400"
                />
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white/70">Mínimo Share (%):</span>
                  <span className="font-mono text-purple-300 font-semibold">{tune.dominantMinShare}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={60}
                  step={5}
                  value={tune.dominantMinShare}
                  onChange={(e) => onUpdateTune('dominantMinShare', Number(e.target.value))}
                  className="w-full h-1.5 bg-slate-700 rounded-lg cursor-pointer accent-purple-400"
                />
              </div>
            </>
          )}

          {currentAlgorithm === 'saturated' && (
            <>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white/70">Brillo Mínimo:</span>
                  <span className="font-mono text-purple-300 font-semibold">{tune.satMinBright}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={200}
                  step={5}
                  value={tune.satMinBright}
                  onChange={(e) => onUpdateTune('satMinBright', Number(e.target.value))}
                  className="w-full h-1.5 bg-slate-700 rounded-lg cursor-pointer accent-purple-400"
                />
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-white/70">Saturación Mínima (%):</span>
                  <span className="font-mono text-purple-300 font-semibold">{tune.satMinSat}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={90}
                  step={5}
                  value={tune.satMinSat}
                  onChange={(e) => onUpdateTune('satMinSat', Number(e.target.value))}
                  className="w-full h-1.5 bg-slate-700 rounded-lg cursor-pointer accent-purple-400"
                />
              </div>
            </>
          )}

          {currentAlgorithm === 'dynamic' && (
            <div className="p-3 rounded-xl bg-purple-950/30 border border-purple-500/20 text-xs text-purple-200">
              <div className="flex items-center gap-1.5 font-medium mb-1 text-purple-300">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>Auto-Calibración Dinámica</span>
              </div>
              <p className="text-[11px] text-white/60 leading-relaxed">
                El Mix dinámico procesa la escena en bloques percentiles con memoria temporal. Se auto-calibra automáticamente cuadro a cuadro.
              </p>
            </div>
          )}

          {(currentAlgorithm === 'average' || currentAlgorithm === 'median') && (
            <div className="p-3 rounded-xl bg-white/5 border border-white/10 text-xs text-white/60">
              Este modo estadístico no requiere ajustes manuales adicionales.
            </div>
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-medium text-xs transition-colors"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
};
